import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, MessageCircle, Mountain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { useWishlist } from "@/context/WishlistContext";
import { Heart, UserRound } from "lucide-react";
import { useCustomer } from "@/context/CustomerContext";
import { useSettings } from "@/context/SettingsContext";
import { fileUrl, buildWaLink, LOGO } from "@/lib/api";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Trips", to: "/trips" },
  { label: "Treks", to: "/trips?category=treks" },
  { label: "Departures", to: "/calendar" },
  { label: "About", to: "/about" },
  { label: "Refer & Earn", to: "/refer" },
  { label: "Gift a Trip", to: "/gift" },
  { label: "Contact", to: "/contact" },
];

export function Navbar() {
  const wishCount = useWishlist().ids.length;
  const { customer } = useCustomer();
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const isHome = loc.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = true;
  const waMsg = `Hi ${settings.brand_name || "Safarshala"}, I'd like to know more about your upcoming trips.`;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 glass bg-background/85 border-b border-border`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
          <img src={fileUrl(settings.logo_url) || LOGO} alt="Safarshala — Where Journeys Become Stories" className="h-11 sm:h-[52px] w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.slice(0, 5).map((n) => (
            <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={`px-3 py-2 text-sm font-medium rounded-full transition-colors hover:text-primary ${solid ? "text-foreground/80" : "text-white/90"}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/wishlist" className={`relative h-9 w-9 rounded-full flex items-center justify-center transition-colors hover:bg-muted/60 ${solid ? "text-foreground/80" : "text-white"}`} data-testid="nav-wishlist" aria-label="Wishlist">
            <Heart className={`h-5 w-5 ${wishCount ? "fill-red-500 text-red-500" : ""}`} />
            {wishCount > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-gold text-black text-[10px] font-semibold flex items-center justify-center" data-testid="nav-wishlist-count">{wishCount}</span>}
          </Link>
          <Link to={customer ? "/account" : "/account/login"} className={`relative h-9 rounded-full flex items-center justify-center gap-1.5 px-2 transition-colors hover:bg-muted/60 ${solid ? "text-foreground/80" : "text-white"}`} data-testid="nav-account" aria-label="My account">
            {customer?.picture ? <img src={customer.picture} alt="" className="h-7 w-7 rounded-full object-cover" /> : <UserRound className="h-5 w-5" />}
            {customer && <span className="hidden md:inline text-sm font-medium max-w-[90px] truncate">{customer.name.split(" ")[0]}</span>}
          </Link>
          <ThemeToggle />
          <a href={buildWaLink(settings.whatsapp_number, waMsg)} target="_blank" rel="noreferrer" className="hidden sm:block" data-testid="nav-whatsapp">
            <Button variant="outline" className="rounded-full border-[#25D366] text-[#1da851] hover:bg-[#25D366]/10 hover:text-[#1da851]">
              <MessageCircle className="mr-1 h-4 w-4" /> WhatsApp
            </Button>
          </a>
          <Link to="/trips" className="hidden sm:block">
            <Button className="rounded-full" data-testid="nav-explore">Explore Trips</Button>
          </Link>

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden rounded-full" data-testid="nav-hamburger">
                <Menu className={solid ? "text-foreground" : "text-white"} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-8 flex flex-col gap-1">
                {NAV.filter((n, i) => n.label !== "Departures").map((n) => (
                  <SheetClose asChild key={n.label}>
                    <Link to={n.to} className="px-3 py-3 text-lg font-heading rounded-lg hover:bg-muted" data-testid={`mnav-${n.label.toLowerCase()}`}>{n.label}</Link>
                  </SheetClose>
                ))}
                <div className="mt-4 grid gap-2">
                  <a href={buildWaLink(settings.whatsapp_number, waMsg)} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="w-full rounded-full border-[#25D366] text-[#1da851]"><MessageCircle className="mr-1 h-4 w-4" /> Query on WhatsApp</Button>
                  </a>
                  <SheetClose asChild><Link to="/trips"><Button className="w-full rounded-full">Explore Trips</Button></Link></SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
